import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

export function buildScenarioPromptsPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  return `Generate a **Scenario Prompts ("What Would You Say?")** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}. This block appears near the end — the learner has practiced vocabulary, conversations, and reading. They should now apply everything freely.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}

## Requirements

1. Generate **3 scenario cards**.
2. Each scenario has:
   - \`situation\`: A vivid scenario description in ${source?.name} (2-3 sentences, second person "you")
   - \`situationTarget\`: A brief version in ${target?.name} (1 sentence)
   - \`emoji\`: A contextual emoji
   - \`suggestedAnswer\`: A model response in ${target?.name} for the answer key (2-3 sentences)
   - \`helpfulVocabulary\`: 4 useful words/phrases in ${target?.name} as prompts
3. Scenarios should build on themes and vocabulary from earlier in the workbook.
4. Each scenario should be a different social situation requiring practical communication.
5. **CRITICAL:** \`instructions\` MUST be in ${source?.name} (never in ${target?.name}). \`instructionsNative\` MUST be in ${target?.name}.

## Output Format

Return a single JSON object matching:

\`\`\`typescript
interface ScenarioPromptsBlock {
  id: string;
  type: "scenario_prompts";
  order: ${ctx.blockOrder};
  title: string;
  titleNative: string;
  theme: "${ctx.subTheme}";
  estimatedPages: 1.5;
  instructions: string;         // ⚠️ MUST be in ${source?.name} — NEVER in ${target?.name}
  instructionsNative: string;  // ⚠️ MUST be in ${target?.name} — brief companion version
  scenarios: Array<{
    situation: string;           // In ${source?.name}
    situationTarget: string;     // Brief version in ${target?.name}
    emoji: string;
    suggestedAnswer: string;     // Model answer in ${target?.name}
    helpfulVocabulary: string[]; // 4 items in ${target?.name}
  }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'scenario_prompts')}${buildCrossMonthClause(ctx)}`;
}
